import {
  LS_COMPANY_INFO_KEY,
  LS_DCE_FILTER_KEY,
  LS_HOME_PAGE_YEAR_KEY,
  LS_HOME_PAGE_YEAR_USER_SELECTED_KEY,
} from '../constants/common';

const MONTH_LABEL_KEYS = {
  JANUARY: 'January.label',
  FEBRUARY: 'February.label',
  MARCH: 'March.label',
  APRIL: 'April.label',
  MAY: 'May.label',
  JUNE: 'June.label',
  JULY: 'July.label',
  AUGUST: 'August.label',
  SEPTEMBER: 'September.label',
  OCTOBER: 'October.label',
  NOVEMBER: 'November.label',
  DECEMBER: 'December.label',
};

const MONTH_NUMS = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

const MONTH_NAME_TO_NUM = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

export function getCompanyInfoFromStorage() {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
  } catch {
    return {};
  }
}

export function isFinancialYearReporting(companyInfo = {}) {
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  return dataFreqType === 'FY';
}

/** FY dropdown display label; option value stays the end year (e.g. value 2026 → "2025-2026"). */
export function getYearDropdownLabel(year, isFinancialYear) {
  const y = Number(year);
  if (isFinancialYear && !Number.isNaN(y)) {
    return `${y - 1}-${y}`;
  }
  return String(year);
}

/** Graph x-axis only (FY companies): "FY 2027" → "2026-27". */
export function formatFyChartXAxisLabel(xValue, year, isFinancialYear) {
  if (!isFinancialYear) return xValue;

  const s = String(xValue ?? '').trim();
  const fyMatch = s.match(/^FY\s*(\d{4})$/i);
  const endYear =
    year != null && year !== ''
      ? Number(year)
      : fyMatch
        ? Number(fyMatch[1])
        : null;

  if (Number.isFinite(endYear)) {
    return `${endYear - 1}-${String(endYear).slice(-2)}`;
  }

  return xValue;
}

export function isFyYearlyXAxisValue(xValue) {
  return /^FY\s*\d{4}$/i.test(String(xValue ?? '').trim());
}

/** Default end year for year selectors; FY uses fy start month vs current month. */
export function getDefaultSelectableYear(companyInfo = {}) {
  const currentYear = new Date().getFullYear();
  if (!isFinancialYearReporting(companyInfo)) {
    return currentYear;
  }
  const fyStartMonth = MONTH_NAME_TO_NUM[String(companyInfo?.financialYearStart || 'APRIL').toUpperCase()] ?? 4;
  const currentMonth = new Date().getMonth() + 1;
  return currentMonth >= fyStartMonth ? currentYear + 1 : currentYear;
}

export function getMaxSelectableYear() {
  return new Date().getFullYear() + 1;
}

/** Max end year for yearly period pickers (compare filter, etc.). Always includes next end year. */
export function getMaxYearlyPeriodEndYear(companyInfo = {}) {
  if (!isFinancialYearReporting(companyInfo)) {
    return new Date().getFullYear();
  }
  return getMaxSelectableYear();
}

export function getFyPeriodFromCompanyInfo(
  selectedYear,
  financialYearStart = 'APRIL',
  financialYearEnd = 'MARCH'
) {
  const startMonth = MONTH_NAME_TO_NUM[financialYearStart] ?? 4;
  const endMonth = MONTH_NAME_TO_NUM[financialYearEnd] ?? 3;
  const y = Number(selectedYear) || new Date().getFullYear();
  const pad = (n) => String(n).padStart(2, '0');
  let startYear = y;
  let endYear = y;
  if (startMonth > endMonth) {
    startYear = y - 1;
    endYear = y;
  }
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  return {
    periodStart: `${startYear}-${pad(startMonth)}-01`,
    periodEnd: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`,
  };
}

/** Human-readable range for active filter chips (FY months or Jan–Dec calendar year). */
export function formatPeriodRangeForYear(year, companyInfo, t) {
  const y = Number(year);
  const safeY = Number.isFinite(y) ? y : new Date().getFullYear();
  if (isFinancialYearReporting(companyInfo)) {
    const startMonth = companyInfo?.financialYearStart || 'APRIL';
    const endMonth = companyInfo?.financialYearEnd || 'MARCH';
    const startLabel = MONTH_LABEL_KEYS[startMonth]
      ? t(MONTH_LABEL_KEYS[startMonth])
      : t('April.label');
    const endLabel = MONTH_LABEL_KEYS[endMonth]
      ? t(MONTH_LABEL_KEYS[endMonth])
      : t('March.label');
    const sm = MONTH_NUMS[startMonth] ?? 4;
    const em = MONTH_NUMS[endMonth] ?? 3;
    const startY = sm > em ? safeY - 1 : safeY;
    return `${startLabel} ${startY} ${t('HomePage.periodTo.label')} ${endLabel} ${safeY}`;
  }
  return `${t('January.label')} ${safeY} ${t('HomePage.periodTo.label')} ${t('December.label')} ${safeY}`;
}

/** Params for assessments/v2 list: CY uses year; FY uses periodStart/periodEnd. */
export function getListActiveAssessmentsPeriodParams(year, companyInfo = {}) {
  if (year === null || year === undefined || year === '') {
    return { year: null, periodStart: null, periodEnd: null };
  }
  const y = Number(year);
  if (!Number.isFinite(y)) {
    return { year: null, periodStart: null, periodEnd: null };
  }
  if (isFinancialYearReporting(companyInfo)) {
    const { periodStart, periodEnd } = getFyPeriodFromCompanyInfo(
      y,
      companyInfo?.financialYearStart,
      companyInfo?.financialYearEnd
    );
    return { year: null, periodStart, periodEnd };
  }
  return { year: y, periodStart: null, periodEnd: null };
}

/** Home (/company_admin/home) and assessments (/company_admin/assessments) period caches only. */
const HOME_AND_ASSESSMENTS_PERIOD_FILTER_KEYS = [
  LS_HOME_PAGE_YEAR_KEY,
  LS_HOME_PAGE_YEAR_USER_SELECTED_KEY,
  LS_DCE_FILTER_KEY,
];

/** Clears persisted year/period filters for Home and Assessments after company FY settings change. */
export function clearPersistedPeriodFilters() {
  if (typeof localStorage === 'undefined') return;
  HOME_AND_ASSESSMENTS_PERIOD_FILTER_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore quota / private mode errors
    }
  });
}
